import type { CapabilityVisibility } from '@/types/capability-types';

/** Plan 58 default when visibility is omitted. */
export const DEFAULT_CAPABILITY_VISIBILITY: CapabilityVisibility = 'account';

export const CAPABILITY_VISIBILITY_VALUES: readonly CapabilityVisibility[] = [
    'global',
    'organization',
    'application',
    'account',
    'user',
] as const;

export const CAPABILITY_VISIBILITY_META: {
    value: CapabilityVisibility;
    label: string;
    description: string;
}[] = [
    { value: 'global', label: 'Global', description: 'Visible to all users across all organizations' },
    { value: 'organization', label: 'Organization', description: 'Visible to all members of your organization' },
    { value: 'application', label: 'Application', description: 'Scoped to the parent application only' },
    { value: 'account', label: 'Account', description: 'Visible within your account' },
    { value: 'user', label: 'User', description: 'Private to you only' },
];

const CANONICAL = new Set<string>(CAPABILITY_VISIBILITY_VALUES);

/**
 * Map stored / legacy / alias visibility tokens onto the Plan 58 five-level enum.
 * Accepts ingest-only aliases: public→global, private→user, team|shared|organisation→organization.
 */
export function normalizeCapabilityVisibility(
    v: string | undefined | null,
): CapabilityVisibility {
    const x = (v ?? DEFAULT_CAPABILITY_VISIBILITY).toLowerCase().trim();
    if (x === 'public') return 'global';
    if (x === 'private') return 'user';
    if (x === 'team' || x === 'shared' || x === 'organisation') return 'organization';
    if (CANONICAL.has(x)) return x as CapabilityVisibility;
    return DEFAULT_CAPABILITY_VISIBILITY;
}

export function isGlobalVisibility(v: string | undefined | null): boolean {
    return normalizeCapabilityVisibility(v) === 'global';
}

/** True when grants are meaningful (author-only / user-scoped capabilities). */
export function isGrantGatedVisibility(v: string | undefined | null): boolean {
    return normalizeCapabilityVisibility(v) === 'user';
}
